'use client';

import React, { useLayoutEffect, useRef } from 'react';
import { loadGsap } from '@/lib/gsapLazy';
import { cn } from '@/lib/twUtils';
import { MOTION_OK, prefersReducedMotion } from '@/lib/motion';

export interface RevealProps {
  as?: React.ElementType;
  className?: string;
  children: React.ReactNode;
  /**
   * Selettore CSS dei figli da far entrare in sequenza. Se assente entra il
   * contenitore stesso come blocco unico.
   */
  stagger?: string;
  /** Ritardo fra un figlio e il successivo (s) */
  step?: number;
  delay?: number;
  /** Spostamento verticale iniziale (px) */
  y?: number;
  /** Punto di attivazione di ScrollTrigger */
  start?: string;
  id?: string;
}

/**
 * Ingresso in scroll con GSAP, caricato a richiesta (`lib/gsapLazy`). Lo
 * stato iniziale lo mette un `opacity: 0` in linea nel `useLayoutEffect`,
 * quindi non c'è sfarfallio nemmeno mentre GSAP arriva; senza JavaScript o
 * con movimento ridotto il contenuto resta semplicemente visibile.
 */
const Reveal = ({
  as: Tag = 'div',
  className,
  children,
  stagger,
  step = 0.09,
  delay = 0,
  y = 20,
  start = 'top 85%',
  id,
}: RevealProps) => {
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || prefersReducedMotion()) return;

    const targets: HTMLElement[] = stagger
      ? Array.from(root.querySelectorAll<HTMLElement>(stagger))
      : [root];
    if (targets.length === 0) return;

    const show = () => targets.forEach(target => (target.style.opacity = ''));
    targets.forEach(target => (target.style.opacity = '0'));

    let cancelled = false;
    let mm: gsap.MatchMedia | undefined;
    loadGsap()
      .then(({ gsap }) => {
        if (cancelled) return;
        mm = gsap.matchMedia();
        mm.add(MOTION_OK, () => {
          gsap.set(targets, { opacity: 0, y });
          gsap.to(targets, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            delay,
            ease: 'power2.out',
            stagger: stagger ? step : 0,
            scrollTrigger: { trigger: root, start, once: true },
          });
        });
      })
      .catch(show);

    return () => {
      cancelled = true;
      mm?.revert();
      show();
    };
  }, [stagger, step, delay, y, start]);

  return (
    <Tag id={id} ref={ref} className={cn(className)}>
      {children}
    </Tag>
  );
};

export default Reveal;
